import React, { useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import type { Lead, LeadStatus } from '../../store/types';

export const LEAD_STATUSES: { key: LeadStatus; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'consult_booked', label: 'Consult booked' },
  { key: 'consult_done', label: 'Consult done' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
];

interface Props {
  lead: Lead;
  isNew: boolean;
  onClose: () => void;
}

const LeadEditor: React.FC<Props> = ({ lead, isNew, onClose }) => {
  const { upsertLead, deleteLead, convertLeadToClient, showToast } = useFitStore();

  const [name, setName] = useState(lead.name);
  const [email, setEmail] = useState(lead.email || '');
  const [phone, setPhone] = useState(lead.phone || '');
  const [source, setSource] = useState(lead.source || '');
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [consultAt, setConsultAt] = useState(lead.consultAt || '');
  const [followUpAt, setFollowUpAt] = useState(lead.followUpAt || '');
  const [notes, setNotes] = useState(lead.notes || '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = () => {
    if (!name.trim()) {
      showToast('Give the lead a name', 'error');
      return;
    }
    upsertLead({
      ...lead,
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      source: source.trim() || undefined,
      status,
      consultAt: consultAt || undefined,
      followUpAt: followUpAt || undefined,
      notes: notes.trim() || undefined,
    });
    showToast(isNew ? 'Lead added' : 'Lead saved');
    onClose();
  };

  const convert = () => {
    convertLeadToClient(lead.id);
    showToast(`${lead.name} converted to a client`);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="card flex flex-col" style={{ maxHeight: '88vh' }}>
          <div className="flex items-start justify-between gap-3 mb-1">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Lead name"
              className="bg-transparent text-xl font-bold tracking-tight outline-none flex-1 min-w-0 border-b border-transparent focus:border-fit-line-strong pb-0.5"
            />
            <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-lg leading-none shrink-0">×</button>
          </div>
          <p className="mono-label mb-4">{isNew ? 'New lead' : 'Lead'}</p>

          {/* status pills */}
          <div className="flex items-center gap-1 flex-wrap mb-4">
            {LEAD_STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatus(s.key)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
                  status === s.key ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto no-scrollbar flex-1 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="mb-4"><p className="mono-label mb-1.5">Email</p>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="—" className="input" /></div>
              <div className="mb-4"><p className="mono-label mb-1.5">Phone</p>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="—" className="input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="mb-4 col-span-1"><p className="mono-label mb-1.5">Source</p>
                <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="IG, referral…" className="input" /></div>
              <div className="mb-4"><p className="mono-label mb-1.5">Consult date</p>
                <input type="date" value={consultAt} onChange={(e) => setConsultAt(e.target.value)} className="input" /></div>
              <div className="mb-4"><p className="mono-label mb-1.5">Follow up</p>
                <input type="date" value={followUpAt} onChange={(e) => setFollowUpAt(e.target.value)} className="input" /></div>
            </div>
            <div className="mb-2"><p className="mono-label mb-1.5">Notes</p>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Consultation notes, objections, what to send next…" className="input" style={{ minHeight: 88 }} /></div>
          </div>

          {/* footer */}
          <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-fit-line">
            {!isNew ? (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-fit-text-secondary">Delete?</span>
                  <button onClick={() => { deleteLead(lead.id); showToast('Lead deleted'); onClose(); }} className="text-[12px] font-semibold text-white underline underline-offset-2">Yes</button>
                  <button onClick={() => setConfirmDelete(false)} className="text-[12px] text-fit-muted hover:text-white">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="text-[12px] text-fit-muted hover:text-white">Delete</button>
              )
            ) : <span />}
            <div className="flex items-center gap-2">
              {!isNew && status !== 'won' && (
                <button onClick={convert} className="btn btn-glass btn-sm">Convert to client ›</button>
              )}
              <button onClick={save} className="btn btn-primary btn-sm">{isNew ? 'Add lead' : 'Save'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadEditor;
